import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { scrapeHistory as scrapeHistoryApi } from "@/lib/api";
import { cn } from "@/lib/utils";

const BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "/api";

// ─── Extraction types ─────────────────────────────────────────────────────────

const EXTRACTION_TYPES = [
  { id: "markdown",  label: "Markdown",   icon: "file-star" },
  { id: "links",     label: "Links",      icon: "link-01" },
  { id: "branding",  label: "Branding",   icon: "colour-swatch" },
  { id: "snapshot",  label: "Snapshot",   icon: "camera-01" },
  { id: "json",      label: "JSON",       icon: "source-code-square-01" },
  { id: "content",   label: "Raw HTML",   icon: "code" },
  { id: "crawl",     label: "Crawl Site", icon: "radar-01" },
] as const;

type ExtractionId = typeof EXTRACTION_TYPES[number]["id"];

interface ExtractionResult {
  type: ExtractionId;
  url: string;
  data: unknown;
  elapsedMs: number;
  error?: string;
}

interface HistoryEntry {
  id: string;
  url: string;
  types: string[];
  ts: string;
  results: ExtractionResult[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ScrapePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeDomain = searchParams.get("domain"); // selected domain filter
  const [url, setUrl] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<Set<ExtractionId>>(new Set(["markdown", "links"]));
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<ExtractionResult[]>([]);
  const [activeResultTab, setActiveResultTab] = useState<string>("");
  const [crawlJobs, setCrawlJobs] = useState<Record<string, { jobId: string; status: string; records?: unknown[] }>>({});
  const [crawlLimit, setCrawlLimit] = useState(10);
  const [useBrowserApi, setUseBrowserApi] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonPrompt, setJsonPrompt] = useState("");
  const [jsonSchema, setJsonSchema] = useState(`{\n  "type": "object",\n  "properties": {\n    "title": { "type": "string" }\n  }\n}`);

  // History — API-backed with localStorage session cache for full results
  const [history, setHistory] = useState<HistoryEntry[]>(() => {
    try { return JSON.parse(localStorage.getItem("scrape_history") ?? "[]"); } catch { return []; }
  });
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Load history from API on mount — merges with localStorage session data
  useEffect(() => {
    scrapeHistoryApi.list(50).then((res) => {
      if ("data" in res && res.data) {
        // Get full results from localStorage session cache
        const sessionCache: HistoryEntry[] = (() => {
          try { return JSON.parse(localStorage.getItem("scrape_history") ?? "[]"); } catch { return []; }
        })();

        // Build a map of API entries by ID
        const apiEntries: HistoryEntry[] = res.data.map((row) => {
          const cached = sessionCache.find(c => c.id === row.id);
          return cached ?? {
            id: row.id,
            url: row.url,
            types: [row.page_type],
            ts: row.scraped_at,
            results: [{
              type: row.page_type as ExtractionId,
              url: row.url,
              data: { summary: row.analysis_summary, score: row.analysis_score, contentLength: row.content_length },
              elapsedMs: row.crawl_ms ?? 0,
            }],
          };
        });

        // Merge: keep session-only entries (not yet in API) + API entries
        // Session entries have numeric IDs (Date.now()), API entries have "sh_" prefix
        const apiIds = new Set(res.data.map(r => r.id));
        const sessionOnlyEntries = sessionCache.filter(c => !apiIds.has(c.id));

        // Combine: session-only first (most recent), then API entries
        // Deduplicate by ID
        const merged = [...sessionOnlyEntries, ...apiEntries];
        const seen = new Set<string>();
        const deduped = merged.filter(e => {
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        });

        setHistory(deduped.slice(0, 100));
      }
      setHistoryLoading(false);
    }).catch(() => setHistoryLoading(false));
  }, []);

  const authHeaders = () => ({
    "Content-Type": "application/json",
  });

  const toggleType = (id: ExtractionId) => {
    if (id === "json") {
      // Open config modal when selecting JSON
      if (!selectedTypes.has("json")) setJsonModalOpen(true);
      else {
        setSelectedTypes(prev => {
          const next = new Set(prev);
          if (next.size > 1) next.delete("json");
          return next;
        });
        return;
      }
    }
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(id)) { if (next.size > 1) next.delete(id); }
      else next.add(id);
      return next;
    });
  };

  const confirmJsonType = () => {
    setSelectedTypes(prev => new Set([...prev, "json"]));
    setJsonModalOpen(false);
  };

  const handleRun = async () => {
    if (!url) return;
    setIsRunning(true);
    setResults([]);
    setCrawlJobs({});

    const types = Array.from(selectedTypes);
    const newResults: ExtractionResult[] = [];

    await Promise.all(types.map(async (type) => {
      try {
        let endpoint = "";
        let body: Record<string, unknown> = { url };

        switch (type) {
          case "markdown": endpoint = "/scrape/markdown"; body = { url, useBrowserApi }; break;
          case "links":    endpoint = "/scrape/links"; break;
          case "content":  endpoint = "/scrape/content"; body = { url, useBrowserApi }; break;
          case "branding": endpoint = "/scrape/branding"; break;
          case "snapshot":
            endpoint = "/scrape/screenshot";
            body = { url, fullPage: true, breakpoints: ["desktop", "tablet", "mobile"] };
            break;
          case "json":
            endpoint = "/scrape/json";
            body = { url, prompt: jsonPrompt || undefined, schema: jsonSchema ? JSON.parse(jsonSchema) : undefined };
            break;
          case "crawl":
            endpoint = "/scrape/crawl";
            body = { url, limit: crawlLimit, depth: 3, formats: ["markdown"] };
            break;
        }

        const res = await fetch(`${BASE}${endpoint}`, {
          method: "POST",
          headers: authHeaders(),
          credentials: "include",
          body: JSON.stringify(body),
        });

        const json = await res.json() as Record<string, unknown>;

        if (type === "crawl" && json.jobId) {
          setCrawlJobs(prev => ({ ...prev, crawl: { jobId: json.jobId as string, status: "running" } }));
          newResults.push({ type, url, data: { jobId: json.jobId, status: "running" }, elapsedMs: 0 });
          pollCrawl(json.jobId as string);
        } else {
          newResults.push({
            type, url,
            data: json,
            elapsedMs: (json.elapsedMs as number) ?? 0,
            error: !res.ok ? (json.message as string) : undefined,
          });
        }
      } catch (e) {
        newResults.push({ type, url, data: null, elapsedMs: 0, error: e instanceof Error ? e.message : "Failed" });
      }
    }));

    setResults(newResults);
    setActiveResultTab(newResults[0]?.type ?? "");
    setIsRunning(false);

    // Save to history — prepend new entry, keep all existing unique entries
    const entry: HistoryEntry = { id: Date.now().toString(), url, types, ts: new Date().toISOString(), results: newResults };
    setHistory(prev => {
      // Deduplicate by ID — new entry goes first
      const existing = prev.filter(e => e.id !== entry.id);
      const next = [entry, ...existing].slice(0, 100);
      // Persist full results to localStorage
      localStorage.setItem("scrape_history", JSON.stringify(next));
      return next;
    });
    setSelectedHistoryId(entry.id);
  };

  const pollCrawl = async (jobId: string) => {
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        const res = await fetch(`${BASE}/scrape/crawl/${jobId}?limit=1`, { headers: authHeaders(), credentials: "include" });
        const d = await res.json() as { status: string };
        setCrawlJobs(prev => ({ ...prev, crawl: { ...prev.crawl, status: d.status } }));
        if (d.status !== "running") {
          const full = await fetch(`${BASE}/scrape/crawl/${jobId}?limit=${crawlLimit}`, { headers: authHeaders(), credentials: "include" });
          const fd = await full.json() as { records?: unknown[] };
          setCrawlJobs(prev => ({ ...prev, crawl: { jobId, status: d.status, records: fd.records ?? [] } }));
          setResults(prev => prev.map(r => r.type === "crawl" ? { ...r, data: { jobId, status: d.status, records: fd.records ?? [] } } : r));
          toast.success(`Crawl complete — ${fd.records?.length ?? 0} pages`);
          return;
        }
      } catch { break; }
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Scrape" />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left history panel ── */}
        <div className="w-64 shrink-0 flex flex-col border-r border-border overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border h-14">
            {activeDomain ? (
              <button
                onClick={() => setSearchParams({}, { replace: true })}
                className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="arrow-left-01" size={10} />
                All sites
              </button>
            ) : (
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Sites</span>
            )}
            {history.length > 0 && !activeDomain && (
              <button
                onClick={() => {
                  if (confirm("Clear all history?")) {
                    setHistory([]);
                    localStorage.removeItem("scrape_history");
                    setSelectedHistoryId(null);
                    setResults([]);
                  }
                }}
                className="font-mono text-[11px] text-muted-foreground hover:text-destructive transition-colors"
              >
                clear
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-hide">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="refresh" size={16} className="text-muted-foreground/30 animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center px-4">
                <Icon name="clock-01" size={24} className="text-muted-foreground/30" />
                <p className="font-sans text-xs text-muted-foreground">No history yet</p>
              </div>
            ) : !activeDomain ? (
              // ── Domain list view ──
              // Show one row per unique domain
              Object.entries(
                history.reduce((acc, entry) => {
                  try {
                    const domain = new URL(entry.url).hostname.replace(/^www\./, "");
                    if (!acc[domain]) acc[domain] = { domain, count: 0, latest: entry.ts };
                    acc[domain].count++;
                    if (entry.ts > acc[domain].latest) acc[domain].latest = entry.ts;
                  } catch {}
                  return acc;
                }, {} as Record<string, { domain: string; count: number; latest: string }>)
              )
                .sort(([, a], [, b]) => b.latest.localeCompare(a.latest))
                .map(([domain, info]) => (
                  <button
                    key={domain}
                    onClick={() => {
                      setSearchParams({ domain }, { replace: true });
                      // Pre-load the most recent run for this domain
                      const latest = history
                        .filter(e => { try { return new URL(e.url).hostname.replace(/^www\./, "") === domain; } catch { return false; } })
                        .sort((a, b) => b.ts.localeCompare(a.ts))[0];
                      if (latest) {
                        setSelectedHistoryId(latest.id);
                        setResults(latest.results);
                        setActiveResultTab(latest.results[0]?.type ?? "");
                        setUrl(latest.url);
                      }
                    }}
                    className="flex w-full items-center gap-3 px-4 py-3 hover:bg-accent transition-colors border-b border-border/30 text-left"
                  >
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                      alt=""
                      className="h-6 w-6 rounded-md border border-border shrink-0 bg-muted"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[11px] text-foreground truncate">{domain}</p>
                      <p className="font-mono text-[11px] text-muted-foreground/60">
                        {info.count} run{info.count !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <Icon name="arrow-right-01" size={11} className="text-muted-foreground shrink-0" />
                  </button>
                ))
            ) : (
              // ── Domain detail view — all runs for this domain ──
              (() => {
                const domainRuns = history
                  .filter(e => { try { return new URL(e.url).hostname.replace(/^www\./, "") === activeDomain; } catch { return false; } })
                  .sort((a, b) => b.ts.localeCompare(a.ts));

                return domainRuns.length === 0 ? (
                  <p className="px-4 py-6 font-sans text-xs text-muted-foreground">No runs for this domain</p>
                ) : (
                  <div>
                    {domainRuns.map((entry) => (
                      <button
                        key={entry.id}
                        onClick={() => {
                          setSelectedHistoryId(entry.id);
                          setResults(entry.results);
                          setActiveResultTab(entry.results[0]?.type ?? "");
                          setUrl(entry.url);
                          // Append entry id to URL
                          setSearchParams({ domain: activeDomain, run: entry.id }, { replace: true });
                        }}
                        className={cn(
                          "flex w-full items-start gap-3 px-4 py-3 hover:bg-accent transition-colors border-b border-border/30 text-left",
                          selectedHistoryId === entry.id ? "bg-accent" : ""
                        )}
                      >
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <p className="font-mono text-[11px] text-foreground truncate">
                            {new URL(entry.url).pathname || "/"}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="font-mono text-[11px] text-muted-foreground/60">
                              {new Date(entry.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </p>
                            <div className="flex gap-1">
                              {entry.types.slice(0, 3).map(t => (
                                <span key={t} className="font-mono text-[11px] text-muted-foreground/50 border border-border/50 rounded px-1">
                                  {t.slice(0, 3)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        {selectedHistoryId === entry.id && (
                          <div className="h-1.5 w-1.5 rounded-full bg-foreground shrink-0 mt-1.5" />
                        )}
                      </button>
                    ))}
                  </div>
                );
              })()
            )}
          </div>

          {/* Footer — Dashboard link */}
          <div className="border-t border-border p-3">
            <Link to="/dashboard" className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-accent transition-colors">
              <Icon name="dashboard-square-01" size={13} className="text-muted-foreground shrink-0" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Dashboard</span>
            </Link>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">

          {/* ── URL input card ── */}
          <div className="rounded-xl border border-border bg-card transition-all">
            {/* URL row */}
            <div className="flex items-center px-4 pt-4 pb-3">
              <span className="font-mono text-xs text-muted-foreground shrink-0 select-none pr-0.5">https://</span>
              <input
                value={url.replace(/^https?:\/\//, "")}
                onChange={(e) => setUrl(e.target.value ? "https://" + e.target.value : "")}
                onKeyDown={(e) => e.key === "Enter" && handleRun()}
                placeholder="competitor.com/pricing"
                className="flex-1 bg-transparent font-mono text-xs text-foreground placeholder:text-muted-foreground/40 focus:outline-none h-12"
              />
            </div>

            {/* Footer bar */}
            <div className="flex items-center justify-between px-4 pb-3 pt-2 border-t border-border/50 gap-3">
              {/* Left — extraction type dropdown */}
              <div className="flex items-center gap-2">

                {/* Extraction type popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 font-mono text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                      <Icon name="flash" size={10} />
                      {selectedTypes.size} format{selectedTypes.size !== 1 ? "s" : ""}
                      <Icon name="arrow-down-01" size={10} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" align="start" className="w-56 p-1.5 space-y-0.5">
                    {EXTRACTION_TYPES.map((type) => {
                      const active = selectedTypes.has(type.id);
                      return (
                        <button
                          key={type.id}
                          onClick={() => toggleType(type.id)}
                          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 hover:bg-accent transition-colors text-left"
                        >
                          {/* Checkbox */}
                          <div className={cn(
                            "h-4 w-4 shrink-0 rounded border flex items-center justify-center transition-colors",
                            active ? "border-foreground bg-foreground" : "border-border bg-transparent"
                          )}>
                            {active && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                  className="text-background" />
                              </svg>
                            )}
                          </div>
                          <span className="font-mono text-[11px] uppercase tracking-widest flex-1 text-foreground">{type.label}</span>
                          {type.id === "json" && active && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); setJsonModalOpen(true); }}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setJsonModalOpen(true); } }}
                              className="font-mono text-[11px] text-muted-foreground hover:text-foreground ml-1 cursor-pointer"
                            >
                              edit
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {selectedTypes.has("crawl") && (
                      <div className="px-3 py-2 border-t border-border mt-1">
                        <p className="font-mono text-[11px] text-muted-foreground mb-1.5">Max pages</p>
                        <div className="flex gap-1">
                          {[5, 10, 25, 50].map((n) => (
                            <button key={n} onClick={() => setCrawlLimit(n)}
                              className={cn(
                                "rounded-lg border px-2 py-0.5 font-mono text-[11px] transition-colors",
                                crawlLimit === n ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:border-muted-foreground"
                              )}>
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Right — run button */}
              <div className="flex items-center gap-2 shrink-0 ml-3">
                {/* Protected site toggle */}
                <button
                  onClick={() => setUseBrowserApi(!useBrowserApi)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-lg border px-2.5 py-1 font-mono text-[11px] transition-colors",
                    useBrowserApi
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
                      : "border-border text-muted-foreground hover:border-muted-foreground"
                  )}
                  title="Enable for sites with bot protection (Cloudflare, etc.)"
                >
                  <Icon name="shield-01" size={10} />
                  {useBrowserApi ? "Protected" : "Standard"}
                </button>

                <Button onClick={handleRun} disabled={isRunning || !url} size="sm" className="shrink-0 rounded-lg">
                  {isRunning ? (
                    <><Icon name="refresh" size={12} className="mr-1.5 animate-spin" />Running...</>
                  ) : (
                    <><Icon name="magic-wand-01" size={12} className="mr-1.5" />Scrape</>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* ── Results ── */}
          {results.length > 0 && (
            <Tabs value={activeResultTab} onValueChange={setActiveResultTab}>
              <TabsList>
                {results.map((r) => {
                  const typeDef = EXTRACTION_TYPES.find(t => t.id === r.type);
                  return (
                    <TabsTrigger key={r.type} value={r.type}>
                      {typeDef?.label ?? r.type}
                      {r.error && <span className="h-1.5 w-1.5 rounded-full bg-red-500 ml-1.5" />}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {results.map((r) => (
                <TabsContent key={r.type} value={r.type} className="mt-4">
                  <ResultView result={r} crawlJob={crawlJobs[r.type]} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
        </div>
      </div>

      {/* ── JSON config modal ── */}
      <Dialog open={jsonModalOpen} onOpenChange={setJsonModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Structured JSON extraction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                Extraction prompt
              </Label>
              <Input
                value={jsonPrompt}
                onChange={(e) => setJsonPrompt(e.target.value)}
                placeholder="e.g. Extract all pricing tiers, prices, and features"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                JSON Schema (optional)
              </Label>
              <Textarea
                value={jsonSchema}
                onChange={(e) => setJsonSchema(e.target.value)}
                className="font-mono text-[11px]"
                rows={8}
                spellCheck={false}
              />
              <p className="font-sans text-xs text-muted-foreground">
                Define the shape of the data. The AI will return JSON matching this schema.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJsonModalOpen(false)}>Cancel</Button>
            <Button onClick={confirmJsonType}>
              <Icon name="checkmark-circle-01" size={12} className="mr-2" />
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Result view ──────────────────────────────────────────────────────────────

function ResultView({ result, crawlJob }: {
  result: ExtractionResult;
  crawlJob?: { jobId: string; status: string; records?: unknown[] };
}) {
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<"rendered" | "raw">("rendered");

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (result.error) {
    return (
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <Icon name="alert-01" size={14} className="text-red-400" />
            <CardTitle>Error</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <p className="font-sans text-xs text-red-400">{result.error}</p>
        </CardContent>
      </Card>
    );
  }

  // ── Crawl ──
  if (result.type === "crawl") {
    const job = crawlJob ?? (result.data as { jobId: string; status: string; records?: unknown[] });
    const records = job?.records ?? [];
    const isRunning = job?.status === "running";
    return (
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <CardTitle>Crawl</CardTitle>
            <Badge variant={isRunning ? "warning" : "success"}>{job?.status ?? "started"}</Badge>
            {!isRunning && records.length > 0 && <Badge variant="secondary">{records.length} pages</Badge>}
            {isRunning && <Icon name="refresh" size={12} className="text-amber-400 animate-spin" />}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isRunning ? (
            <p className="p-5 font-sans text-xs text-muted-foreground">Crawling... polling every 5 seconds.</p>
          ) : records.length > 0 ? (
            <div className="divide-y divide-border max-h-96 overflow-y-auto scrollbar-hide">
              {(records as { url: string; status: string; markdown?: string; metadata?: { title?: string } }[]).map((r, i) => (
                <div key={i} className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-0.5">
                    <div className={cn("h-1.5 w-1.5 rounded-full shrink-0", r.status === "completed" ? "bg-emerald-500" : "bg-red-500")} />
                    <span className="font-sans text-xs font-medium truncate">{r.metadata?.title ?? r.url}</span>
                  </div>
                  <p className="font-mono text-[11px] text-muted-foreground truncate pl-3.5">{r.url}</p>
                  {r.markdown && <p className="font-sans text-xs text-muted-foreground mt-1 line-clamp-2 pl-3.5">{r.markdown.slice(0, 120)}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="p-5 font-sans text-xs text-muted-foreground">No records yet.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Snapshot ──
  if (result.type === "snapshot") {
    const d = result.data as {
      id: string; url: string; capturedAt: string;
      breakpoints: Record<string, { screenshot: string; width: number; height: number; label: string }>;
    };
    const bps = Object.entries(d.breakpoints ?? {});
    const [activeBp, setActiveBp] = useState(bps[0]?.[0] ?? "desktop");
    const current = d.breakpoints?.[activeBp];

    return (
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>Snapshots</CardTitle>
            <div className="flex gap-1 rounded-lg border border-border p-0.5">
              {bps.map(([bp, data]) => (
                <button key={bp} onClick={() => setActiveBp(bp)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors",
                    activeBp === bp ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  )}>
                  <Icon name={bp === "mobile" ? "smartphone-01" : bp === "tablet" ? "tablet-01" : "computer"} size={10} />
                  {data.label}
                </button>
              ))}
            </div>
            {current && (
              <a href={current.screenshot} download={`${d.url.replace(/https?:\/\//, "")}-${activeBp}.png`}>
                <Button variant="ghost" size="sm">
                  <Icon name="download-01" size={12} className="mr-1.5" />Download
                </Button>
              </a>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {current ? (
            <div className={cn("mx-auto overflow-hidden", activeBp === "mobile" ? "max-w-sm" : activeBp === "tablet" ? "max-w-2xl" : "w-full")}>
              <img src={current.screenshot} alt={`${activeBp} screenshot`} className="w-full"
                onError={(e) => { (e.target as HTMLImageElement).alt = "Screenshot unavailable"; }} />
            </div>
          ) : (
            <p className="p-5 font-sans text-xs text-muted-foreground">No screenshot data.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Branding ──
  if (result.type === "branding") {
    const d = result.data as {
      branding: {
        brandName?: string; tagline?: string;
        colors?: { hex: string; name: string; usage: string }[];
        fonts?: { name: string; usage: string; weight: string }[];
        logo?: {
          description?: string; style?: string; url?: string;
          faviconUrl?: string;
          variants?: { url?: string; type?: string; background?: string }[];
        };
        ogImages?: { url: string; alt?: string; width?: string; height?: string }[];
        toneOfVoice?: string; targetAudience?: string; valueProposition?: string;
        keyMessages?: string[];
        socialLinks?: { twitter?: string; linkedin?: string; github?: string };
      };
      elapsedMs: number;
    };
    const b = d.branding ?? {};

    // Build logo variants — combine extracted + favicon fallback
    const domain = result.url.replace(/^https?:\/\//, "").split("/")[0];
    const faviconUrl = b.logo?.faviconUrl ?? `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    const logoVariants: { label: string; src: string; bg: string }[] = [];

    if (b.logo?.url) logoVariants.push({ label: "Primary", src: b.logo.url, bg: "bg-background" });
    if (b.logo?.variants?.length) {
      b.logo.variants.forEach((v: { url?: string; type?: string; background?: string }) => {
        if (v.url) logoVariants.push({ label: v.type ?? "Variant", src: v.url, bg: v.background === "dark" ? "bg-zinc-900" : "bg-background" });
      });
    }
    // Always add favicon as fallback
    logoVariants.push({ label: "Favicon", src: faviconUrl, bg: "bg-background" });
    // Deduplicate by src
    const uniqueLogos = logoVariants.filter((v, i, arr) => arr.findIndex(x => x.src === v.src) === i);

    return (
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>{b.brandName ?? "Branding"}</CardTitle>
            <Badge variant="secondary">{d.elapsedMs}ms</Badge>
          </div>
          {b.tagline && <p className="font-sans text-xs text-muted-foreground mt-1 italic">"{b.tagline}"</p>}
        </CardHeader>
        <CardContent className="p-5 space-y-6">
          {/* Logo variants */}
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Logo</p>
            <div className="flex flex-wrap gap-3">
              {uniqueLogos.map((logo, i) => (
                <div key={i} className="space-y-1.5">
                  <div className={cn(
                    "flex items-center justify-center rounded-xl border border-border p-4 w-32 h-20",
                    logo.bg
                  )}>
                    <img
                      src={logo.src}
                      alt={logo.label}
                      className="max-h-12 max-w-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                        (e.target as HTMLImageElement).parentElement!.innerHTML =
                          `<span class="font-mono text-[11px] text-muted-foreground">Not found</span>`;
                      }}
                    />
                  </div>
                  {/* Dark bg variant */}
                  <div className="flex items-center justify-center rounded-xl border border-border p-4 w-32 h-20 bg-zinc-900">
                    <img
                      src={logo.src}
                      alt={`${logo.label} dark`}
                      className="max-h-12 max-w-full object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  </div>
                  <p className="font-mono text-[11px] text-center text-muted-foreground">{logo.label}</p>
                </div>
              ))}
            </div>
            {b.logo?.description && (
              <p className="font-sans text-xs text-muted-foreground">{b.logo.description} · {b.logo.style}</p>
            )}
          </div>
          {/* OG Images */}
          {b.ogImages && b.ogImages.length > 0 && (
            <div className="space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">OG Images</p>
              <div className="grid grid-cols-2 gap-3">
                {b.ogImages.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-border">
                    <img src={img.url} alt={img.alt ?? `OG image ${i + 1}`}
                      className="w-full h-32 object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                    {img.alt && <p className="px-2.5 py-1.5 font-sans text-xs text-muted-foreground truncate">{img.alt}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {b.colors && b.colors.length > 0 && (            <div className="space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Colors</p>
              <div className="flex flex-wrap gap-3">
                {b.colors.map((c, i) => (
                  <div key={i} className="flex items-center gap-2.5 rounded-xl border border-border px-3 py-2">
                    <div className="h-8 w-8 rounded-lg border border-border/50 shrink-0 shadow-sm"
                      style={{ backgroundColor: c.hex }} />
                    <div>
                      <p className="font-mono text-[11px] font-medium">{c.hex}</p>
                      <p className="font-sans text-xs text-muted-foreground">{c.name}</p>
                      <p className="font-sans text-xs text-muted-foreground/60">{c.usage}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fonts */}
          {b.fonts && b.fonts.length > 0 && (
            <div className="space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Typography</p>
              <div className="grid grid-cols-2 gap-2">
                {b.fonts.map((f, i) => (
                  <div key={i} className="rounded-xl border border-border px-4 py-3">
                    <p className="font-sans text-base font-semibold">{f.name}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">{f.usage} · {f.weight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tone + Audience */}
          <div className="grid grid-cols-2 gap-3">
            {b.toneOfVoice && (
              <div className="rounded-xl border border-border p-3 space-y-1">
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Tone of voice</p>
                <p className="font-sans text-xs">{b.toneOfVoice}</p>
              </div>
            )}
            {b.targetAudience && (
              <div className="rounded-xl border border-border p-3 space-y-1">
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Target audience</p>
                <p className="font-sans text-xs">{b.targetAudience}</p>
              </div>
            )}
          </div>

          {/* Value prop */}
          {b.valueProposition && (
            <div className="rounded-xl border border-border p-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Value proposition</p>
              <p className="font-sans text-xs leading-relaxed">{b.valueProposition}</p>
            </div>
          )}

          {/* Key messages */}
          {b.keyMessages && b.keyMessages.length > 0 && (
            <div className="space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Key messages</p>
              <div className="space-y-1.5">
                {b.keyMessages.map((m, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Icon name="checkmark-circle-01" size={13} className="text-emerald-500 shrink-0 mt-0.5" />
                    <p className="font-sans text-xs text-foreground/80">{m}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Social links */}
          {b.socialLinks && Object.values(b.socialLinks).some(Boolean) && (
            <div className="space-y-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Social</p>
              <div className="flex gap-2">
                {Object.entries(b.socialLinks).filter(([, v]) => v).map(([platform, link]) => (
                  <a key={platform} href={link!} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 font-mono text-[11px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
                    <Icon name={platform === "twitter" ? "twitter" : platform === "linkedin" ? "linkedin-01" : "github"} size={12} />
                    {platform}
                  </a>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Links ──
  if (result.type === "links") {
    const d = result.data as { links: string[]; count: number; elapsedMs: number };
    return (
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>{d.count} links</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{d.elapsedMs}ms</Badge>
              <Button variant="ghost" size="sm" onClick={() => copy(d.links.join("\n"))}>
                <Icon name={copied ? "checkmark-circle-01" : "copy-01"} size={12} className="mr-1.5" />
                Copy all
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-96 overflow-y-auto scrollbar-hide divide-y divide-border">
            {d.links.map((link, i) => (
              <a
                key={i}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 px-5 py-2.5 hover:bg-muted/30 transition-colors"
              >
                <Icon name="link-01" size={11} className="text-muted-foreground shrink-0" />
                <span className="font-mono text-[11px] text-muted-foreground group-hover:text-foreground truncate flex-1 transition-colors">
                  {link}
                </span>
                <Icon
                  name="arrow-right-01"
                  size={12}
                  className="text-muted-foreground shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150"
                />
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── JSON ──
  if (result.type === "json") {
    const d = result.data as { result: unknown; elapsedMs: number };
    const formatted = JSON.stringify(d.result, null, 2);
    return (
      <Card>
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>JSON</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{d.elapsedMs}ms</Badge>
              <Button variant="ghost" size="sm" onClick={() => copy(formatted)}>
                <Icon name={copied ? "checkmark-circle-01" : "copy-01"} size={12} className="mr-1.5" />
                Copy
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <pre className="overflow-x-auto p-5 font-mono text-[11px] text-emerald-400 leading-relaxed bg-muted/20 rounded-b-lg whitespace-pre-wrap max-h-[500px] overflow-y-auto scrollbar-hide">
            {formatted}
          </pre>
        </CardContent>
      </Card>
    );
  }

  // ── Markdown / HTML ──
  const d = result.data as { markdown?: string; html?: string; elapsedMs: number; length?: number };
  const rawText = d.markdown ?? d.html ?? "";
  const isHtml = result.type === "content";

  return (
    <Card>
      <CardHeader className="border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>{isHtml ? "Raw HTML" : "Markdown"}</CardTitle>
            <div className="flex gap-0.5 rounded-lg border border-border p-0.5">
              {(["rendered", "raw"] as const).map((v) => (
                <button key={v} onClick={() => setView(v)}
                  className={cn(
                    "rounded-md px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors",
                    view === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  )}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{d.elapsedMs}ms</Badge>
            {d.length && <Badge variant="secondary">{(d.length / 1000).toFixed(1)}k</Badge>}
            <Button variant="ghost" size="sm" onClick={() => copy(rawText)}>
              <Icon name={copied ? "checkmark-circle-01" : "copy-01"} size={12} className="mr-1.5" />
              Copy
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {view === "rendered" && !isHtml ? (
          <div className="markdown-content max-h-[600px] overflow-y-auto scrollbar-hide">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1>{children}</h1>,
                h2: ({ children }) => <h2>{children}</h2>,
                h3: ({ children }) => <h3>{children}</h3>,
                p: ({ children }) => <p>{children}</p>,
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                ),
                ul: ({ children }) => <ul>{children}</ul>,
                ol: ({ children }) => <ol>{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                blockquote: ({ children }) => <blockquote>{children}</blockquote>,
                code: ({ children, className }) => (
                  <code className={className}>{children}</code>
                ),
                pre: ({ children }) => <pre>{children}</pre>,
                table: ({ children }) => <div className="markdown-table"><table>{children}</table></div>,
                th: ({ children }) => <th>{children}</th>,
                td: ({ children }) => <td>{children}</td>,
                hr: () => <hr />,
                img: ({ src, alt }) => <img src={src} alt={alt ?? ""} loading="lazy" />,
              }}
            >
              {rawText}
            </ReactMarkdown>
          </div>
        ) : (
          <pre className={cn(
            "overflow-x-auto p-5 font-mono text-[11px] leading-relaxed bg-muted/20 rounded-b-lg whitespace-pre-wrap max-h-[600px] overflow-y-auto scrollbar-hide",
            isHtml ? "text-blue-400" : "text-muted-foreground"
          )}>
            {rawText}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}
